import React, { useMemo, useState } from 'react';
import { Card, Typography, Select, Space, Tooltip, Empty, Checkbox } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';

const { Title, Text } = Typography;
const { Option } = Select;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('tr');

const HourlyMetricsComparisonTable = ({ data, previousData, loading }) => {
  const [selectedMetrics, setSelectedMetrics] = useState(['revenue', 'totalCost', 'orders']);

  // Mevcut metrikleri al
  const availableMetrics = useMemo(() => {
    if (!data || data.length === 0) return [];
    const metrics = new Set();
    
    // Temel metrikler
    metrics.add('revenue');
    metrics.add('orders'); 
    metrics.add('productSales');
    
    // Maliyet detayları
    data.forEach(item => {
      if (item.data && typeof item.data === 'object') {
        Object.keys(item.data).forEach(key => {
          metrics.add(key);
        });
      }
    });
    
    // totalCost'u manuel ekle
    metrics.add('totalCost');
    
    return Array.from(metrics).sort();
  }, [data]);

  // Tablo verisi hazırla - transpose edilmiş format (mevcut ve önceki veri için)
  const { tableData, previousTableData } = useMemo(() => {
    const processData = (dataSet) => {
      if (!dataSet || dataSet.length === 0 || selectedMetrics.length === 0) return { hours: [], days: [], data: {} };

      const hoursData = {};
      const daysSet = new Set();
      
      // Veriyi işle
      dataSet.forEach(item => {
        const date = dayjs.utc(item.date).tz('Europe/Istanbul');
        const day = date.format('DD.MM');
        const hour = date.hour();
        const dayFull = date.format('YYYY-MM-DD');
        
        daysSet.add(dayFull);
        
        if (!hoursData[hour]) {
          hoursData[hour] = {};
        }
        
        if (!hoursData[hour][dayFull]) {
          hoursData[hour][dayFull] = {};
        }
        
        // Her metrik için değerleri hesapla
        selectedMetrics.forEach(metric => {
          let value = 0;
          
          if (metric === 'totalCost') {
            // Toplam maliyeti hesapla
            const metaCost = Number(item.data?.metaCost) || 0;
            const googleCost = Number(item.data?.googleCost) || 0;
            const metaBoostCost = Number(item.data?.metaBoostCost) || 0;
            value = metaCost + googleCost + metaBoostCost;
          } else if (item.data && item.data[metric] !== undefined) {
            value = Number(item.data[metric]) || 0;
          }
          
          hoursData[hour][dayFull][metric] = value;
        });
      });

      // Günleri sırala
      const sortedDays = Array.from(daysSet).sort();
      
      // Saatleri sırala (0-23)
      const sortedHours = Object.keys(hoursData).map(Number).sort((a, b) => a - b);

      return {
        hours: sortedHours,
        days: sortedDays,
        data: hoursData
      };
    };

    return {
      tableData: processData(data),
      previousTableData: processData(previousData)
    };
  }, [data, previousData, selectedMetrics]);

  // Değişim yüzdesini hesapla
  const calculateChange = (current, previous) => {
    if (current === undefined || current === null) return null;
    if (previous === undefined || previous === null || previous === 0) {
      // Önceki değer yoksa veya 0 ise, mevcut değer varsa 100% artış göster
      return current > 0 ? 100 : null;
    }
    return ((current - previous) / previous) * 100;
  };

  // Metrik değerini formatla
  const formatMetricValue = (value, metric) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    
    if (metric.includes('Cost') || metric === 'revenue' || metric === 'totalCost') {
      // Para birimi - kısaltılmış format
      if (value >= 1000) {
        return `${Math.round(value / 1000)}k`;
      }
      return Math.round(value).toString();
    }
    
    if (metric === 'orders' || metric === 'productSales') {
      return Math.round(value).toString();
    }
    
    return Math.round(value * 100) / 100;
  };

  // Renk hesaplama - metrik tipine göre
  const getMetricColor = (value, metric, allValues) => {
    if (!value || value === 0) return 'transparent';
    
    const values = allValues.filter(v => v > 0);
    if (values.length === 0) return 'transparent';
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    if (min === max) return 'rgba(255, 255, 0, 0.3)';
    
    const normalized = (value - min) / (max - min);
    
    // Revenue için yeşil tonları
    if (metric === 'revenue') {
      const opacity = 0.2 + (normalized * 0.6);
      return `rgba(34, 197, 94, ${opacity})`; // Yeşil
    }
    
    // Maliyet için kırmızı tonları  
    if (metric.includes('Cost') || metric === 'totalCost') {
      const opacity = 0.2 + (normalized * 0.6);
      return `rgba(239, 68, 68, ${opacity})`; // Kırmızı
    }
    
    // Orders için mavi tonları
    if (metric === 'orders' || metric === 'productSales') {
      const opacity = 0.2 + (normalized * 0.6);
      return `rgba(59, 130, 246, ${opacity})`; // Mavi
    }
    
    // Diğerleri için sarı tonları
    const opacity = 0.2 + (normalized * 0.6);
    return `rgba(251, 191, 36, ${opacity})`; // Sarı
  };

  // Her metrik için tüm değerleri topla (renk hesabı için)
  const getAllValuesForMetric = (metric) => {
    const values = [];
    tableData.hours.forEach(hour => {
      tableData.days.forEach(day => {
        const value = tableData.data[hour]?.[day]?.[metric];
        if (value && value > 0) {
          values.push(value);
        }
      });
    });
    return values;
  };

  if (!data || data.length === 0) {
    return (
      <Card>
        <Empty 
          description="Saatlik karşılaştırma verisi bulunamadı"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
          Saatlik Metrik Karşılaştırması
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Her saat için seçili metriklerin günlük karşılaştırması
        </Text>
        
        <Space wrap>
          <Text strong>Gösterilecek Metrikler:</Text>
          {availableMetrics.map(metric => (
            <Checkbox
              key={metric}
              checked={selectedMetrics.includes(metric)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedMetrics([...selectedMetrics, metric]);
                } else {
                  setSelectedMetrics(selectedMetrics.filter(m => m !== metric));
                }
              }}
            >
              {metric}
            </Checkbox>
          ))}
        </Space>
      </div>

      {selectedMetrics.length === 0 ? (
        <Empty description="Lütfen en az bir metrik seçin" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '11px'
          }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '8px 4px', 
                  border: '1px solid #f0f0f0',
                  background: '#fafafa',
                  textAlign: 'center',
                  minWidth: '60px',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2
                }}>
                  Saat
                </th>
                {tableData.days.map(day => (
                  <th key={day} 
                    colSpan={selectedMetrics.length}
                    style={{ 
                      padding: '4px',
                      border: '1px solid #f0f0f0',
                      background: '#fafafa',
                      textAlign: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                    {dayjs(day).format('DD.MM')}
                    <div style={{ fontSize: '9px', color: '#666', fontWeight: 'normal' }}>
                      {dayjs(day).format('ddd')}
                    </div>
                  </th>
                ))}
              </tr>
              
              {/* Alt başlık - metrik isimleri */}
              <tr>
                <th style={{ 
                  padding: '4px', 
                  border: '1px solid #f0f0f0',
                  background: '#f5f5f5',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2
                }}></th>
                {tableData.days.map(day => (
                  selectedMetrics.map((metric, idx) => (
                    <th key={`${day}-${metric}`} style={{ 
                      padding: '2px',
                      border: '1px solid #f0f0f0',
                      background: '#f5f5f5',
                      textAlign: 'center',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      minWidth: '35px',
                      color: metric === 'revenue' ? '#059669' :
                             metric.includes('Cost') || metric === 'totalCost' ? '#dc2626' :
                             metric === 'orders' || metric === 'productSales' ? '#2563eb' : '#d97706'
                    }}>
                      {metric.replace('Cost', '').replace('revenue', 'Rev').replace('orders', 'Ord').replace('productSales', 'PS').replace('totalCost', 'Total')}
                    </th>
                  ))
                ))}
              </tr>
            </thead>
            
            <tbody>
              {tableData.hours.map(hour => (
                <tr key={hour}>
                  <td style={{ 
                    padding: '6px 4px',
                    border: '1px solid #f0f0f0',
                    background: '#fafafa',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1
                  }}>
                    {hour.toString().padStart(2, '0')}:00
                  </td>
                  
                  {tableData.days.map(day => (
                    selectedMetrics.map(metric => {
                      const value = tableData.data[hour]?.[day]?.[metric];
                      
                      // Önceki güne denk gelen günü bul
                      const currentDate = dayjs(day);
                      const previousDate = currentDate.subtract(1, 'day');
                      const previousDay = previousDate.format('YYYY-MM-DD');
                      
                      const previousValue = tableData.data[hour]?.[previousDay]?.[metric];
                      const change = calculateChange(value, previousValue);
                      const allValues = getAllValuesForMetric(metric);
                      const hasValue = value && value > 0;
                      const hasChange = change !== null;
                      const isPositive = hasChange && change > 0;
                      const isNegative = hasChange && change < 0;
                      
                      return (
                        <td key={`${day}-${metric}`} style={{ 
                          padding: '2px',
                          border: '1px solid #f0f0f0',
                          textAlign: 'center',
                          background: hasValue ? getMetricColor(value, metric, allValues) : 'transparent',
                          cursor: hasValue ? 'pointer' : 'default',
                          fontSize: '10px',
                          fontWeight: hasValue ? 'bold' : 'normal'
                        }}>
                          {hasValue ? (
                            <Tooltip 
                              title={
                                <div>
                                  <div><strong>{metric}</strong></div>
                                  <div>Tarih: {dayjs(day).format('DD.MM.YYYY')} {hour.toString().padStart(2, '0')}:00</div>
                                  <div>Değer: {value.toLocaleString('tr-TR')}</div>
                                  <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                                    Önceki gün ({previousDate.format('DD.MM.YYYY')}): {previousValue ? previousValue.toLocaleString('tr-TR') : 'veri yok'}
                                  </div>
                                  {hasChange && (
                                    <div style={{ 
                                      color: isPositive ? '#52c41a' : isNegative ? '#f5222d' : '#666',
                                      marginTop: 4 
                                    }}>
                                      {isPositive ? '↗' : isNegative ? '↘' : '→'} {change.toFixed(1)}% (önceki güne göre)
                                    </div>
                                  )}
                                </div>
                              }
                            >
                              <div>
                                {formatMetricValue(value, metric)}
                                {hasChange ? (
                                  <div style={{ 
                                    fontSize: '8px',
                                    lineHeight: '1',
                                    color: isPositive ? '#52c41a' : isNegative ? '#f5222d' : '#666',
                                    marginTop: 1
                                  }}>
                                    {isPositive ? '↗' : isNegative ? '↘' : '→'}{Math.abs(change).toFixed(0)}%
                                  </div>
                                ) : (
                                  <div style={{ 
                                    fontSize: '7px',
                                    lineHeight: '1',
                                    color: '#ccc',
                                    marginTop: 1
                                  }}>
                                    ≈
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </td>
                      );
                    })
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Renk açıklaması */}
      <div style={{ 
        marginTop: 16, 
        padding: '8px',
        background: '#fafafa',
        borderRadius: '4px',
        fontSize: '11px'
      }}>
        <Text strong>Renk Kodları: </Text>
        <Space>
          <span style={{ color: '#059669' }}>■ Revenue (Yeşil)</span>
          <span style={{ color: '#dc2626' }}>■ Maliyet (Kırmızı)</span>
          <span style={{ color: '#2563eb' }}>■ Sipariş (Mavi)</span>
          <span style={{ color: '#d97706' }}>■ Diğer (Sarı)</span>
        </Space>
      </div>
    </Card>
  );
};

export default HourlyMetricsComparisonTable;